import React from "react";
import DocumentMeta from "./DocumentMeta";
import { ROUTE_META } from "./routeMeta";

const sectionClass = "text-2xl font-semibold mt-6 mb-2";
const paragraphClass = "mb-4";

const PrivacyPDFTools = () => (
  <div className="max-w-3xl mx-auto px-6 py-16 text-gray-800">
    <DocumentMeta
      title={ROUTE_META.pdfToolsPrivacy.title}
      description={ROUTE_META.pdfToolsPrivacy.description}
      robots={ROUTE_META.pdfToolsPrivacy.robots}
    />
    <h1 className="text-4xl font-bold mb-6">Privacy Policy for PDF Toolbox by Appazzola</h1>
    <p className={paragraphClass}>
      <strong>Effective date:</strong> April 1, 2026
      <br />
      <strong>Last updated:</strong> April 1, 2026
      <br />
      <strong>App:</strong> PDF Toolbox (the "App")
      <br />
      <strong>Operator:</strong> Appazzola LLC, 231 E Canal St, Hershey, PA 17033 ("Appazzola", "we", "us", "our")
      <br />
      <strong>General contact:</strong> contact@appazzola.com
      <br />
      <strong>Privacy and legal contact:</strong> legal@appazzola.com
    </p>

    <p className={paragraphClass}>
      PDF Toolbox is designed to process your documents on your device. We do not run a
      document-upload service for the core PDF tools in this version of the app.
    </p>

    <h2 className={sectionClass}>Scope</h2>
    <p className={paragraphClass}>
      This Privacy Policy explains how PDF Toolbox by Appazzola handles information when you
      use the mobile application and related features.
    </p>
    <p className={paragraphClass}>
      If a platform operator such as Apple or Google collects data through its own storefront,
      billing, or device services, that data is governed by that platform operator&apos;s own
      privacy terms.
    </p>
    <p className={paragraphClass}>
      This Privacy Policy applies to information processed through the app, related in-app legal
      screens, and supporting operational services used to run the app.
    </p>

    <h2 className={sectionClass}>What We Collect</h2>
    <p className={paragraphClass}>
      We do not require you to create an account to use the app.
    </p>
    <p className={paragraphClass}>
      We do not intentionally collect the contents of your PDFs, images, annotations,
      signatures, text you compose in the app, or exported files on our own servers for the
      app&apos;s core document tools.
    </p>
    <p className={paragraphClass}>
      The app stores certain information locally on your device, such as recent files, app
      settings, onboarding state, and files you place in the app&apos;s working storage.
    </p>
    <p className={paragraphClass}>
      Depending on your use of the app, third-party service providers may also process limited
      technical or billing-related data as described below.
    </p>

    <h2 className={sectionClass}>How Document Processing Works</h2>
    <p className={paragraphClass}>
      Core features such as viewing, merging, splitting, compressing, annotating, signing,
      converting images to PDF, and converting supported text or HTML documents to PDF are
      intended to run locally on your device.
    </p>
    <p className={paragraphClass}>
      Because processing is local in this version of the app, your document content is not
      uploaded to Appazzola servers as part of normal use of those tools.
    </p>
    <p className={paragraphClass}>
      If you choose to share or export a file using device or platform tools, that export or
      transfer is controlled by the destination you select.
    </p>

    <h2 className={sectionClass}>Permissions</h2>
    <p className={paragraphClass}>
      The app may request access to your camera so you can capture pages and create PDFs.
    </p>
    <p className={paragraphClass}>
      The app may request access to your photo library or files so you can import images and
      documents, and may request permission to save exported content when you choose to do so.
    </p>
    <p className={paragraphClass}>
      You can control app permissions in your device settings, but some features will not work
      without the required access.
    </p>
    <p className={paragraphClass}>
      The app requests permissions only for features that depend on them, and permission
      availability may vary by platform and operating-system version.
    </p>

    <h2 className={sectionClass}>Payments and Subscriptions</h2>
    <p className={paragraphClass}>
      In-app purchases and subscriptions are provided through the Apple App Store or Google Play,
      with RevenueCat used to help manage entitlement state across supported platforms.
    </p>
    <p className={paragraphClass}>
      Those providers may process transaction identifiers, subscription status, purchase history,
      device identifiers, and other billing-related metadata needed to complete purchases,
      restore purchases, prevent fraud, and determine whether premium access is active.
    </p>
    <p className={paragraphClass}>
      We do not receive your full payment card number from in-app purchases handled by Apple or Google.
    </p>
    <p className={paragraphClass}>
      Purchase restoration, entitlement checks, and related premium access state may involve
      limited transaction or device metadata processed by the store provider and RevenueCat.
    </p>

    <h2 className={sectionClass}>Analytics and Crash Reporting</h2>
    <p className={paragraphClass}>
      The app may use Firebase Analytics and Firebase Crashlytics to help us understand feature
      usage, app stability, and operational issues.
    </p>
    <p className={paragraphClass}>
      Those services may process device identifiers, app instance identifiers, basic event data,
      screen or interaction telemetry, crash traces, diagnostic information, and similar
      technical metadata needed to measure app performance and investigate failures.
    </p>
    <p className={paragraphClass}>
      We do not use Firebase Analytics or Crashlytics to intentionally collect the contents of
      the documents you process with the app&apos;s core PDF tools.
    </p>
    <p className={paragraphClass}>
      You can reduce some analytics or advertising-related data sharing through device-level
      privacy controls where supported by your platform.
    </p>

    <h2 className={sectionClass}>Advertising</h2>
    <p className={paragraphClass}>
      The free version of the app may display ads using Google Mobile Ads or similar
      ad-delivery providers.
    </p>
    <p className={paragraphClass}>
      Ad providers may receive device and interaction data that is commonly used to deliver,
      measure, and limit ads, subject to your device settings, platform permissions, consent
      choices where applicable, and the provider&apos;s own privacy terms.
    </p>
    <p className={paragraphClass}>
      Your document contents are not provided to ad networks for ad targeting through the
      app&apos;s core PDF tools.
    </p>
    <p className={paragraphClass}>
      Advertising behavior may vary by platform, region, consent status, and app configuration.
    </p>

    <h2 className={sectionClass}>Third-Party Services</h2>
    <p className={paragraphClass}>
      The app may rely on third-party providers such as Apple, Google, RevenueCat, Firebase,
      and Google Mobile Ads to support purchases, subscriptions, analytics, crash reporting,
      advertising, and related app operations.
    </p>
    <p className={paragraphClass}>
      Those third parties operate under their own terms, privacy notices, data practices, and
      retention rules. We encourage you to review their policies if you use related features.
    </p>

    <h2 className={sectionClass}>Data Sharing</h2>
    <p className={paragraphClass}>We do not sell your document contents.</p>
    <p className={paragraphClass}>
      We may rely on service providers that process limited data on our behalf to support
      purchases, ads, analytics, crash reporting, hosting of app metadata, legal compliance,
      fraud prevention, or customer support.
    </p>
    <p className={paragraphClass}>
      We may also disclose information if required by law, to protect rights or safety, or in
      connection with a business transfer such as a merger, acquisition, or asset sale.
    </p>

    <h2 className={sectionClass}>Your Choices</h2>
    <p className={paragraphClass}>You can stop using the app at any time.</p>
    <p className={paragraphClass}>
      You can delete locally stored files, clear app data, uninstall the app, or change device
      permissions to limit future data processing on your device.
    </p>
    <p className={paragraphClass}>
      You can manage subscriptions, renewals, and refunds through the app store account used to
      make the purchase, subject to store rules.
    </p>
    <p className={paragraphClass}>
      If you contact us with a privacy request, we may need reasonable information to verify and
      respond to the request.
    </p>

    <h2 className={sectionClass}>Retention</h2>
    <p className={paragraphClass}>
      Files and settings stored by the app remain on your device until you delete them,
      uninstall the app, clear app data, or otherwise remove them through device controls.
    </p>
    <p className={paragraphClass}>
      Third-party providers such as Apple, Google, RevenueCat, and ad partners may retain billing
      or ad-related records under their own retention practices.
    </p>
    <p className={paragraphClass}>
      Crash or analytics records processed by service providers may also be retained under those
      providers&apos; standard retention settings and operational practices.
    </p>

    <h2 className={sectionClass}>Security</h2>
    <p className={paragraphClass}>
      We use reasonable administrative, technical, and organizational measures appropriate to the
      nature of the app and the limited data we directly control.
    </p>
    <p className={paragraphClass}>
      No system or transmission method is completely secure, and we cannot guarantee absolute security.
    </p>

    <h2 className={sectionClass}>Children</h2>
    <p className={paragraphClass}>
      The app is not directed to children under 13, and we do not knowingly collect personal
      information from children under 13 through our own services.
    </p>

    <h2 className={sectionClass}>International Use</h2>
    <p className={paragraphClass}>
      If a third-party provider processes limited purchase or advertising data for the app, that
      provider may process data in countries other than your own, subject to its own safeguards and policies.
    </p>

    <h2 className={sectionClass}>Changes to This Policy</h2>
    <p className={paragraphClass}>
      We may update this Privacy Policy from time to time. If we make material changes, we may
      update the in-app policy, the store listing, or both. The updated effective date will appear
      at the top of this policy.
    </p>

    <h2 className={sectionClass}>Contact</h2>
    <p className={paragraphClass}>
      For privacy questions or requests, contact{" "}
      <a className="text-teal-600 underline" href="mailto:contact@appazzola.com">
        contact@appazzola.com
      </a>
      .
    </p>
  </div>
);

export default PrivacyPDFTools;
